import { Injectable, Logger, OnApplicationShutdown, OnModuleInit } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { AuditContextState } from './audit-context.service';
import { AuditWriterService } from './audit-writer.service';

/** How often provisional headers get their counts filled in and empties pruned. */
const RECONCILE_INTERVAL_MS = 10_000;
/** Grace period before a header with no detail rows is treated as empty. */
const EMPTY_GRACE_SECONDS = 30;

/**
 * Maintains `audit.change_batch` — one row per user action, which is what the
 * admin change list reads.
 *
 * The header exists so the grouped list is a plain indexed scan of one row per
 * action, rather than `GROUP BY batchId ORDER BY MAX(changedOn) DESC` over the
 * multi-million-row detail table. That aggregate cannot be served from an index
 * and gets slower every month, on the page most likely to be opened.
 *
 * Counts are filled in afterwards, not at write time: the rows are produced by
 * database triggers, so the request has no idea how many there were, and
 * counting them inline would put a query back on the request path — the exact
 * thing this design exists to avoid. Headers are therefore provisional until
 * the reconciler confirms them, and one that never gets any detail rows (a
 * login, a no-op save) is deleted rather than left cluttering the list.
 */
@Injectable()
export class AuditBatchService implements OnModuleInit, OnApplicationShutdown {
  private readonly logger = new Logger(AuditBatchService.name);
  private timer?: NodeJS.Timeout;

  constructor(
    private readonly prisma: PrismaService,
    private readonly writer: AuditWriterService
  ) {}

  onModuleInit(): void {
    this.timer = setInterval(() => {
      void this.reconcile();
    }, RECONCILE_INTERVAL_MS);

    this.timer.unref?.();
  }

  onApplicationShutdown(): void {
    if (this.timer) clearInterval(this.timer);
  }

  /** Queued, never awaited — this runs at the tail of a real request. */
  close(state: AuditContextState): void {
    if (state.headerWritten) return;

    this.writer.enqueue('changeBatch', {
      batchId: state.batchId,
      organizationId: state.organizationId ?? null,
      storeId: state.storeId ?? null,
      actorId: state.actorId ?? null,
      actorRole: state.actorRole ?? null,
      actorName: state.actorName ?? null,
      ipAddress: state.ipAddress ?? null,
      userAgent: state.userAgent ?? null,
      correlationId: state.correlationId ?? null,
      action: state.action ?? null,
      summary: state.summary ?? null,
      route: state.route ?? null,
      method: state.method ?? null,
      source: state.source ?? 'api',
      rowCount: 0,
      tableCount: 0,
      startedAt: new Date(),
      endedAt: new Date(),
    });

    state.headerWritten = true;
  }

  /**
   * One batched UPDATE for every unreconciled header, then one DELETE for the
   * empties — cost is per interval, not per request.
   */
  async reconcile(): Promise<{ counted: number; pruned: number }> {
    try {
      // Many audited tables carry no storeId column of their own, so their
      // trigger rows land with a NULL storeId and would be invisible to every
      // store-scoped query. The batch header knows the store, so adopt it.
      await this.prisma.$executeRawUnsafe(`
        UPDATE audit.change_log l
           SET "storeId"        = COALESCE(l."storeId", b."storeId"),
               "organizationId" = COALESCE(l."organizationId", b."organizationId")
          FROM audit.change_batch b
         WHERE l."batchId" = b."batchId"
           AND l."storeId" IS NULL
           AND b."storeId" IS NOT NULL
      `);

      const counted = await this.prisma.$executeRawUnsafe(`
        UPDATE audit.change_batch b
           SET "rowCount"   = c.n,
               "tableCount" = c.t
          FROM (
            SELECT "batchId", count(*)::int AS n, count(DISTINCT "tableName")::int AS t
              FROM audit.change_log
             WHERE "batchId" IS NOT NULL
               AND "changedOn" > now() - interval '1 hour'
             GROUP BY "batchId"
          ) c
         WHERE b."batchId" = c."batchId"
           AND b."rowCount" IS DISTINCT FROM c.n
      `);

      // Denormalise the merchant category onto the header, so the merchant feed
      // filters and pages on ONE table. Filtering the detail rows instead meant
      // a page of 25 batches could come back nearly empty while thousands of
      // matching actions sat behind it.
      await this.prisma.$executeRawUnsafe(`
        UPDATE audit.change_batch b
           SET "category" = c.category
          FROM (
            SELECT l."batchId",
                   (array_agg(t."merchantCategory" ORDER BY t."merchantCategory"))[1] AS category
              FROM audit.change_log l
              JOIN audit.audited_tables t
                ON t."schemaName" = l."schemaName" AND t."tableName" = l."tableName"
             WHERE l."batchId" IS NOT NULL
               AND l."changedOn" > now() - interval '1 hour'
               AND t."merchantCategory" IS NOT NULL
             GROUP BY l."batchId"
          ) c
         WHERE b."batchId" = c."batchId"
           AND b."category" IS DISTINCT FROM c.category
      `);

      // A request that wrote nothing leaves a header with no detail. Removing
      // them keeps the change list to actions that actually changed something.
      const pruned = await this.prisma.$executeRawUnsafe(`
        DELETE FROM audit.change_batch b
         WHERE b."rowCount" = 0
           AND b."startedAt" < now() - interval '${EMPTY_GRACE_SECONDS} seconds'
           AND NOT EXISTS (
             SELECT 1 FROM audit.change_log l WHERE l."batchId" = b."batchId"
           )
      `);

      return { counted, pruned };
    } catch (error) {
      this.logger.warn(
        `batch reconcile failed: ${error instanceof Error ? error.message : String(error)}`
      );
      return { counted: 0, pruned: 0 };
    }
  }
}
